/**
 * Keeping the screen awake while an import runs.
 *
 * A phone that locks its screen backgrounds the tab, and a backgrounded tab on
 * iOS or Android is throttled and then suspended — taking the worker, its
 * uploads and the whole import with it. The workaround people find on their own
 * is to sit there keeping the display awake with a finger, which is not a
 * feature.
 *
 * A wake lock is released automatically whenever the page is hidden, so it has
 * to be taken again each time the page comes back. Holding one is a real
 * battery cost, which is why it is taken only for the length of an import and
 * released the moment it ends.
 */

type Sentinel = { released: boolean; release: () => Promise<void> };

export const wakeLockSupported = () =>
  typeof navigator !== 'undefined' && 'wakeLock' in navigator;

export function holdScreenAwake(): () => void {
  if (!wakeLockSupported()) return () => {};

  let sentinel: Sentinel | null = null;
  let done = false;

  const take = async () => {
    if (done || document.hidden || sentinel) return;
    try {
      sentinel = await (navigator as unknown as {
        wakeLock: { request: (t: 'screen') => Promise<Sentinel> };
      }).wakeLock.request('screen');
      // The browser drops it on its own terms; notice so we can take it again.
      (sentinel as unknown as EventTarget).addEventListener?.('release', () => { sentinel = null; });
    } catch {
      // Refused — low battery, or a browser that says no. The import still
      // runs; it just needs the screen left on, which the UI says.
      sentinel = null;
    }
  };

  const onVisible = () => { if (!document.hidden) void take(); };
  document.addEventListener('visibilitychange', onVisible);
  void take();

  return () => {
    done = true;
    document.removeEventListener('visibilitychange', onVisible);
    void sentinel?.release().catch(() => {});
    sentinel = null;
  };
}
